/**
 * surfaceRule — the L7 surface decision for a workspace route, as one pure function.
 *
 * The locked rule (Sign-Up & Onboarding Axis 1, L2 + L7): which surface a viewer gets
 * derives from THEIR relationship to the space — never a sharer toggle, never item count.
 *
 *   member                         → full space context
 *   non-member · space `visible`   → the space's public face (welcome landing; nav browsable,
 *                                    RLS filters rows — soft landing per Rob 2026-07-24)
 *   non-member · space `hidden`    → behaves as NOT FOUND (O1): no shell, no nav, no name.
 *     EXCEPTION 1 — the arrival carries an item-scoped credential (?s=<id> with a ?share=/?edit=
 *     token, or an item-share claim link, which uniquely carries ?s= alongside ?ct=): route to
 *     the standalone item page /item/<id>, which has its own gate (ItemAccessGate → O3a/O4).
 *     EXCEPTION 2 (2026-07-27) — the viewer has NO CLAIMED IDENTITY (signed out, or on the
 *     anonymous guest session): they get O3a **"Log in to continue"**, not O1. O1's purpose is to
 *     stop a STRANGER learning the space exists; it was never meant for a *member who is merely
 *     logged out*, and shipping it for them turned every hidden space into a dead end for its own
 *     members — `dss-summit-2026` is hidden with 358 of them, and they hit it just by pasting the
 *     space URL or re-clicking an already-consumed magic link. A generic sign-in prompt reveals
 *     nothing (it names nothing, same as ItemAccessGate's O3a); after sign-in, RLS re-runs and
 *     members land inside. O1 is reserved for a viewer who HAS proven identity and still lacks
 *     access — the case the no-reveal rule is actually about.
 *
 * Pure so the whole decision table is unit-testable without React. Callers feed it live
 * state and render on the result; it never reads globals.
 *
 * Security posture (DP4): RLS already protects rows — this decides SURFACE. The gate
 * replaces content; it never covers it.
 *
 * @param {object} s
 * @param {boolean} s.audienceEnabled  isAudienceEnabled() — false on legacy Airtable spaces
 * @param {boolean} s.singleItemMode   the /item/ route (has its own gate; rule never fires)
 * @param {boolean} s.doorSettled      the space_door_info fetch resolved (success OR failure)
 * @param {string|null} s.doorDiscovery 'hidden' | 'visible' | null (null = RPC failed/absent)
 * @param {boolean} [s.doorUnreachable] the door fetch failed because the SERVER could not be reached
 *                                     (isNetworkFailure), not because it answered with an error
 * @param {boolean} s.accessLoading    effective ctx.loading (membership fetch in flight)
 * @param {boolean} s.isMember         effective membership (preview-aware — an admin
 *                                     "viewing as guest" correctly previews the gate)
 * @param {boolean} s.membershipFailed the membership read ERRORED (network, 401, RLS blip) rather
 *                                     than answering. isMember is false in that case too, but it
 *                                     means "unknown", not "no" — see the hidden branch below.
 * @param {boolean} s.isClaimed        the viewer has a claimed identity (email/phone confirmed).
 *                                     False = signed out OR on the anonymous guest session — either
 *                                     way they cannot be a member, so they get O3a, not O1.
 * @param {boolean} s.ctPresent        the arrival URL carried a ?ct= claim token
 * @param {boolean} s.ctSettled        the claim redemption reached a definitive outcome
 *                                     (redeemed / expired / wrong-account / unredeemable / absent)
 * @param {string|null} s.arrivalItemId the ?s= item id from the arrival URL, if any
 * @param {boolean} s.hasItemToken     the arrival URL carried ?share= or ?edit=
 * @returns {'normal'|'hold'|'o1'|'signin'|'error'|'unreachable'|{redirect: string}}
 */
export function resolveSurface({
  audienceEnabled,
  singleItemMode,
  doorSettled,
  doorDiscovery,
  doorUnreachable = false,
  accessLoading,
  isMember,
  membershipFailed,
  isClaimed,
  ctPresent,
  ctSettled,
  arrivalItemId,
  hasItemToken,
}) {
  // Legacy Airtable spaces have no door model; the /item/ page has its own gate.
  if (!audienceEnabled || singleItemMode) return 'normal';

  // Never decide before the door is known.
  if (!doorSettled) return 'hold';

  // The door is the first read every space makes, and anyone may make it. When it could not even
  // reach the server, nothing after it will either — so the fail-open below would draw the whole
  // shell with every list empty and nothing saying why, which reads as an empty space, or as a
  // members-only space showing a stranger its furniture (seen 2026-10-13 on a local stack whose
  // gateway answered every request with an empty reply). Say the server is unreachable instead.
  // Only a NETWORK failure lands here: a server that answered with an error (an older database
  // without the RPC) still fails open, for the reason given below.
  if (doorUnreachable) return 'unreachable';

  // Visible — the public face is the welcome landing, handled by App's landing effects,
  // inside the normal shell. Also the fail-OPEN branch: a null door (RPC failed) falls
  // through to pre-existing behavior rather than bricking every visible space on a
  // client-local blip (an attacker can't induce the failure remotely; RLS still filters).
  if (doorDiscovery !== 'hidden') return 'normal';

  // Hidden space:
  // A pending claim redemption may flip the viewer to member (space invite) — never
  // flash O1 at a legitimate invitee mid-redeem.
  if (ctPresent && !ctSettled) return 'hold';
  if (accessLoading) return 'hold';
  if (isMember) return 'normal';

  // The L7 exception: an item-scoped arrival gets the standalone item page. ?s= plus a
  // share/edit token is a credential arrival; ?s= plus a (settled) ct is an item-share
  // claim link — item-share emails uniquely carry ?s= alongside ?ct=, space invites don't.
  // A BARE ?s= deep link is NOT a credential → O1.
  if (arrivalItemId && (hasItemToken || ctPresent)) return { redirect: arrivalItemId };

  // No claimed identity → they cannot be a member yet; offer sign-in instead of a dead end.
  // (Checked AFTER accessLoading above, so a still-resolving session can't flash this at a member.)
  if (!isClaimed) return 'signin';

  // The membership read errored instead of answering, so `isMember: false` here means UNKNOWN.
  // O1 is a verdict — "we checked, and you have no access" — and stating it on an unanswered
  // question is how a member of a hidden space got told their own space did not exist, with copy
  // inviting them to go and find a different email address. Say what actually happened instead:
  // 'error' is retryable and names nothing about the space, so it reveals no more than O1 does.
  // Placed LAST, so it can only ever replace an O1 — a claimed member, a credentialed item
  // arrival and a signed-out viewer all keep the surface they had.
  if (membershipFailed) return 'error';

  return 'o1';
}

/**
 * parseArrival — the one place the arrival URL's item-credential shape is interpreted.
 * Capture ONCE on mount: App's URL-cleanup effects can rewrite the URL after applying
 * it, so a later read would miss what the viewer actually arrived with. `ctToken` is the
 * raw claim token — the consent gate redeems with THIS captured value, never a re-read
 * of the (possibly cleaned) URL.
 *
 * @param {string} search  window.location.search
 * @returns {{arrivalItemId: string|null, hasItemToken: boolean, ctPresent: boolean, ctToken: string|null}}
 */
export function parseArrival(search) {
  const p = new URLSearchParams(search || '');
  const ctToken = p.get('ct') || null;
  return {
    arrivalItemId: p.get('s') || null,
    hasItemToken: !!(p.get('share') || p.get('edit')),
    ctPresent: !!ctToken,
    ctToken,
  };
}

/**
 * isAuthFailure — did this read fail because the SIGN-IN is no longer good, rather than because
 * the network was? The two need different words and a different button: "Try again" on an expired
 * credential retries the same rejection forever, which is a loop the viewer cannot leave.
 *
 * PostgREST answers an unusable JWT with 401 and a PGRST301 code, and GoTrue's own refresh
 * failures surface as messages about the token rather than as codes at all — so both are checked.
 * Everything else, including the bare "Failed to fetch" a dropped connection produces, is not an
 * auth failure and keeps the retry.
 *
 * @param {{code?: string|null, message?: string|null}|null} err
 * @returns {boolean}
 */
export function isAuthFailure(err) {
  if (!err) return false;
  if (err.code === 'PGRST301' || err.code === '401') return true;
  return /\bjwt\b|\btoken\b|expired|refresh|not authenticated|unauthorized/i.test(err.message || '');
}

/**
 * isNetworkFailure — did a request fail because the server could not be REACHED, rather than because
 * it answered? A refusal or a bad query is an answer, and the app has a meaning for each; a dropped
 * connection is the absence of one, and reading it as "no rows" is how an outage turns into a space
 * that looks empty.
 *
 * supabase-js reports a fetch that never got a response as status 0 with the TypeError's words in
 * the message ("TypeError: Failed to fetch" in Chromium, "Load failed" in Safari, "NetworkError when
 * attempting to fetch resource" in Firefox). A gateway that is up while the service behind it is not
 * answers 502, 503 or 504, which means the same thing to the person waiting.
 *
 * @param {{status?: number|null, message?: string|null, name?: string|null}|null} err
 * @returns {boolean}
 */
export function isNetworkFailure(err) {
  if (!err) return false;
  if (err.status === 0 || err.status === 502 || err.status === 503 || err.status === 504) return true;
  return /failed to fetch|load failed|networkerror|network request failed|fetch failed|err_[a-z_]+/i.test(
    `${err.name || ''} ${err.message || ''}`,
  );
}
